/**
 * useHarvestWindowAlarm — Lion's Mane için 12-24 saatlik hasat penceresi alarmı.
 *
 * Morning briefing (07:00) dışında gün içinde status 'ready' veya daysLeft <= 1
 * olan Lion's Mane baglerini izler; pencere açıldığında anında Telegram push gönderir.
 *
 * localStorage key: gmc_harvest_alarm_{bagId}_{date} — aynı bag için günde 1 push.
 */

import { useEffect, useRef } from 'react';
import { useHarvestPredictionsV2 } from './useHarvestPredictionV2';
import { sendTelegramMessage } from '../services/notificationService';
import type { AlertNotificationSettings } from '../types';

const CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 dakikada bir kontrol
const LS_PREFIX = 'gmc_harvest_alarm';

interface Opts {
  notifSettings: AlertNotificationSettings;
}

export function useHarvestWindowAlarm({ notifSettings }: Opts): void {
  const predictions = useHarvestPredictionsV2();
  // Stable ref to avoid re-creating interval on every render
  const predictionsRef = useRef(predictions);
  predictionsRef.current = predictions;

  useEffect(() => {
    if (!notifSettings.telegram?.enabled) return;

    const check = async () => {
      const today = new Date().toISOString().slice(0, 10);

      for (const p of predictionsRef.current) {
        if (p.speciesId !== 'lion-mane') continue;
        if (p.status !== 'ready' && p.status !== 'overdue' && p.daysLeft > 1) continue;

        const key = `${LS_PREFIX}_${p.bagId}_${today}`;
        if (localStorage.getItem(key)) continue;

        const urgency = p.status === 'overdue'
          ? `🚨 *GECİKMİŞ* — ${Math.abs(p.daysLeft)} gün geçti`
          : p.daysLeft <= 0
            ? '🍄 *HASAT PENCERESİ AÇIK* — bugün hasat et'
            : '⚠️ *Yarın hasat et* — 24 saat kaldı';

        const lines = [
          urgency,
          ``,
          `📦 Torba: <b>${p.bagName}</b>`,
          `⏱ Fruiting: ${p.daysIn.toFixed(0)}. gün`,
          `📊 GDD ilerleme: ${Math.round(p.gddProgress * 100)}% (model: ${p.modelType})`,
          ``,
          `🦁 Aslan Yelesi hasat penceresi <b>12-24 saat</b> — diken 1-2 cm, beyaz, sararma başlamadan hasat et.`,
        ];

        localStorage.setItem(key, '1');
        await sendTelegramMessage(lines.join('\n'));
      }
    };

    check();
    const id = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [notifSettings.telegram?.enabled]);
}
